import React from 'react'
import { useSelector } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { DealDetail, Instrument, NegotiationDetail, OrderDetail } from 'api'
import { Locale, Translation } from 'locale'
import { InstrumentHelper } from 'model'
import { createFilteredInstrumentsSelector, createInstrumentGetterSelector, sortAndMapInstruments } from 'selectors/instrumentSelectors'
import { Field } from 'view/components/inputs'
import { Combobox, ComboboxProps } from 'view/components/options'
import { useParametricSelector } from 'utils'
import {
  createInstrumentFiltersForSecondaryCollateralSelector,
  SecondaryCollateralSelectorProps
} from 'selectors/instrumentSelectors/instrumentFiltersForSecondaryCollateralSelector'

const DEFAULT_PROPS = {
  getOptionText: InstrumentHelper.getName,
  filterFields: ['isin', 'cusip', 'collateralBasketIsin', 'collateralBasketCusip'],
  noItemsText: Locale.translate(Translation.SC_N_SECONDARY_COLLATERALS, { count: 0 })
}

const secondaryOptionKeysSelector = createSelector(
  createFilteredInstrumentsSelector(
    createInstrumentFiltersForSecondaryCollateralSelector((_: {}, props: SecondaryCollateralSelectorProps) => props)
  ),
  instruments => sortAndMapInstruments(instruments)
)

interface SecondaryCollateralComboboxProps<T> extends Omit<ComboboxProps<Instrument, string>, 'optionKeys' | 'getOption'> {
  detail: T
}

export const StagingSecondaryCollateralCombobox = React.forwardRef(
  ({ detail: orderDetail, ...props }: SecondaryCollateralComboboxProps<OrderDetail>, ref: React.Ref<Combobox>) => {
    const optionKeys = useParametricSelector(secondaryOptionKeysSelector, {
      collateralBasketId: orderDetail?.collateralBasket?.id,
      collateralId: orderDetail?.collateral?.id,
      fundId: orderDetail?.orderFund?.id,
      counterpartyIds: orderDetail?.counterpartyIds ?? [],
      marketType: orderDetail?.order?.marketType
    })
    const getOption = useSelector(createInstrumentGetterSelector)

    return (
      <Combobox
        ref={ref}
        {...props}
        optionKeys={optionKeys}
        getOption={getOption}
        getOptionText={props.getOptionText ?? DEFAULT_PROPS.getOptionText}
        filterFields={props.filterFields ?? DEFAULT_PROPS.filterFields}
        noItemsText={props.noItemsText ?? DEFAULT_PROPS.noItemsText}
      />
    )
  }
)

export const InitiatedSecondaryCollateralCombobox = React.forwardRef(
  ({ detail: negotiationDetail, ...props }: SecondaryCollateralComboboxProps<NegotiationDetail>, ref: React.Ref<Combobox>) => {
    const optionKeys = useParametricSelector(secondaryOptionKeysSelector, {
      collateralBasketId: negotiationDetail?.collateralBasket?.id,
      collateralId: negotiationDetail?.collateral?.id,
      fundId: negotiationDetail?.orderFund?.id,
      counterpartyIds: negotiationDetail?.counterparty?.id ? [negotiationDetail.counterparty.id] : [],
      marketType: negotiationDetail?.negotiation?.marketType
    })
    const getOption = useSelector(createInstrumentGetterSelector)

    return (
      <Combobox
        ref={ref}
        {...props}
        optionKeys={optionKeys}
        getOption={getOption}
        getOptionText={props.getOptionText ?? DEFAULT_PROPS.getOptionText}
        filterFields={props.filterFields ?? DEFAULT_PROPS.filterFields}
        noItemsText={props.noItemsText ?? DEFAULT_PROPS.noItemsText}
      />
    )
  }
)

export const ServicingSecondaryCollateralCombobox = React.forwardRef(
  ({ detail: dealDetail, ...props }: SecondaryCollateralComboboxProps<DealDetail>, ref: React.Ref<Combobox>) => {
    const optionKeys = useParametricSelector(secondaryOptionKeysSelector, {
      collateralBasketId: dealDetail?.parentCollateralBasket?.id ?? dealDetail?.collateralBasket?.id,
      collateralId: dealDetail?.collateral?.id,
      fundId: dealDetail?.orderFund?.id,
      counterpartyIds: dealDetail?.counterparty?.id ? [dealDetail.counterparty.id] : [],
      marketType: dealDetail?.deal?.marketType
    })
    const getOption = useSelector(createInstrumentGetterSelector)

    return (
      <Combobox
        ref={ref}
        {...props}
        optionKeys={optionKeys}
        getOption={getOption}
        getOptionText={props.getOptionText ?? DEFAULT_PROPS.getOptionText}
        filterFields={props.filterFields ?? DEFAULT_PROPS.filterFields}
        noItemsText={props.noItemsText ?? DEFAULT_PROPS.noItemsText}
      />
    )
  }
)

export const ServicingSecondaryCollateralComboboxField = Field(ServicingSecondaryCollateralCombobox)
